import React, { useEffect, useState } from 'react';
import { Box, Flex, TextV2 as Text, InputV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { FilterFacet } from './FilterFacet';
import { StyledSlider, PriceFilterSkeleton } from '../components';
import { useTranslation } from '@shared/core';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { PriceDistributionChart } from './PriceDistributionChart';
import { PRICE_RANGE } from '../../../constants';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

interface PriceRangeFacetProps {
  title: string;
  description: string;
  priceRange: [number, number];
  onPriceRangeChange: (value: number | readonly number[]) => void;
  isLoading?: boolean;
  prices?: number[]; // Array of all prices to show in distribution
}

export const PriceRangeFacet: React.FC<PriceRangeFacetProps> = ({ title, description, priceRange, onPriceRangeChange, isLoading, prices = [] }) => {
  const { t } = useTranslation('accommodationsConsole');
  const filterPanelTrans = t('filterPanel');
  // Track if inputs were manually cleared
  const [isMinCleared, setIsMinCleared] = useState(false);
  const [isMaxCleared, setIsMaxCleared] = useState(false);

  const [minInput, setMinInput] = useState(priceRange[0].toString());
  const [maxInput, setMaxInput] = useState(priceRange[1].toString());

  const handleMinChange = useEventCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMinInput(value);
    setIsMinCleared(value === '');

    const numValue = value === '' ? PRICE_RANGE.MIN : parseInt(value, 10);
    if (value === '' || !isNaN(numValue)) {
      onPriceRangeChange([numValue, priceRange[1]]);
    }
  });

  const handleMaxChange = useEventCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMaxInput(value);
    setIsMaxCleared(value === '');

    const numValue = value === '' ? PRICE_RANGE.MAX : parseInt(value, 10);
    if (value === '' || !isNaN(numValue)) {
      onPriceRangeChange([priceRange[0], numValue]);
    }
  });

  // Update inputs when price range changes
  useEffect(() => {
    // Only update if not manually cleared
    if (!isMinCleared || (isMinCleared && priceRange[0] !== PRICE_RANGE.MIN)) {
      setMinInput(priceRange[0].toString());
      setIsMinCleared(false);
    }
    if (!isMaxCleared || (isMaxCleared && priceRange[1] !== PRICE_RANGE.MAX)) {
      setMaxInput(priceRange[1].toString());
      setIsMaxCleared(false);
    }
  }, [priceRange, isMinCleared, isMaxCleared]);

  return (
    <SkeletonGroup
      isReady={!isLoading}
      placeholder={<PriceFilterSkeleton />}
    >
      <Box maxWidth={{ _: pxToRem(222), sm: 'full' }}>
        <FilterFacet
          title={title}
          description={description}
        >
          <Flex
            gap={3}
            alignItems="center"
            marginTop={7}
          >
            <Box>
              <Text
                typographyVariant="label3"
                marginBottom={3}
              >
                {filterPanelTrans.priceRange.min()}
              </Text>
              <InputV2
                type="number"
                inputMode="numeric"
                value={minInput}
                onChange={handleMinChange}
                placeholder="Min"
                startElement={<Text typographyVariant="body2">$</Text>}
                style={{ paddingLeft: pxToRem(32) }}
                min={0}
              />
            </Box>
            <Text marginTop={pxToRem(24)}>-</Text>
            <Box>
              <Text
                typographyVariant="label3"
                marginBottom={3}
              >
                {filterPanelTrans.priceRange.max()}
              </Text>
              <InputV2
                type="number"
                inputMode="numeric"
                value={maxInput}
                onChange={handleMaxChange}
                placeholder="Max"
                startElement={<Text typographyVariant="body2">$</Text>}
                style={{ paddingLeft: pxToRem(32) }}
                min={0}
              />
            </Box>
          </Flex>
          <Box marginTop={pxToRem(24)}>
            <Box marginBottom={pxToRem(-12)}>
              <PriceDistributionChart prices={prices} />
            </Box>
            <StyledSlider
              value={priceRange}
              onChange={onPriceRangeChange}
              min={PRICE_RANGE.MIN}
              max={PRICE_RANGE.MAX}
              className="slider"
              trackClassName="track"
              thumbClassName="thumb"
            />
          </Box>
        </FilterFacet>
      </Box>
    </SkeletonGroup>
  );
};
